import { useEffect, useMemo, useState } from 'react';
import { useSamMaterial } from './useApi.js';
import { useNfcAgent } from '../nfcagent/hooks.js';
import { getReader } from '../nfcagent/client.js';
function firstReason(...reasons) {
    return reasons.find((reason) => typeof reason === 'string' && reason.length > 0);
}
export function useReaderPrecondition(enabled = true) {
    const materialQuery = useSamMaterial();
    const material = materialQuery.data ?? null;
    const agent = useNfcAgent();
    const [hydratedCard, setHydratedCard] = useState(null);
    const activePiccReader = useMemo(() => agent.readers.find(reader => reader.deviceRole === 'Picc' && reader.cardPresent) ?? null, [agent.readers]);
    const resolvedLastCard = useMemo(() => agent.lastCard ?? hydratedCard, [agent.lastCard, hydratedCard]);
    // A hydrated card is dropped while rendering once it no longer applies: no
    // active reader, another reader, or a tap event already names this reader's card.
    const tappedOnActiveReader = !!activePiccReader
        && agent.lastCard?.readerId === activePiccReader.id && !!agent.lastCard.card.uid;
    if (hydratedCard !== null && (!enabled || !activePiccReader
        || (!!hydratedCard.readerId && hydratedCard.readerId !== activePiccReader.id)
        || tappedOnActiveReader)) {
        setHydratedCard(null);
    }
    useEffect(() => {
        if (!enabled || !activePiccReader || tappedOnActiveReader)
            return;
        let cancelled = false;
        void (async () => {
            try {
                const detail = await getReader(activePiccReader.id);
                if (cancelled || !detail.card?.uid) {
                    return;
                }
                setHydratedCard({ readerId: activePiccReader.id, card: detail.card });
            }
            catch {
                // Fallback hydration is best-effort; standard tap events still drive state.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [activePiccReader, agent.lastCard, enabled, hydratedCard?.readerId, tappedOnActiveReader]);
    const samPresent = !!agent.sam?.present;
    const samBindingMismatch = agent.binding?.matches === false;
    const samSiteBound = agent.binding?.matches === true || agent.sam?.claimVerified === true;
    const openDisabledReason = firstReason(materialQuery.isLoading && 'access.readerPrecondition.materialLoading', materialQuery.isError && 'access.readerPrecondition.materialUnavailable', agent.status === 'disconnected' && 'access.readerPrecondition.agentDisconnected', agent.status === 'keymaster' && 'access.readerPrecondition.keymaster', !samPresent && 'access.readerPrecondition.noSam', samBindingMismatch && 'access.readerPrecondition.samBindingMismatch', !samSiteBound && 'access.readerPrecondition.samNotSiteBound', !agent.sam?.uid && 'access.readerPrecondition.samUidUnavailable');
    const canOpenIssue = !openDisabledReason;
    const submitDisabledReason = firstReason(openDisabledReason, !activePiccReader && 'access.readerPrecondition.placeCard', !resolvedLastCard?.card.uid && 'access.readerPrecondition.cardUidMissing');
    return {
        canOpenIssue,
        openDisabledReason,
        canSubmit: !submitDisabledReason,
        submitDisabledReason,
        readerSnapshot: {
            status: agent.status,
            readers: agent.readers,
            activePiccReader,
            samUid: agent.sam?.uid ?? null,
            samPresent,
            samSiteBound,
            samBindingMismatch,
            material,
            claimId: agent.claimId,
        },
        lastCard: resolvedLastCard,
    };
}
//# sourceMappingURL=useReaderPrecondition.js.map